import axios from 'axios';

import buildPaginationQueryOpts from '@/shared/sort/sorts';

import { IFileGeodetic } from '@/shared/model/file-geodetic.model';

const baseApiUrl = 'api/file-geodetics';
const baseApiMobileUrl = 'api/mobile/file-geodetics';

export default class FileGeodeticService {
  public find(id: number): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public downloadLink(id: number) {
    return `${baseApiUrl}/${id}/download`;
  }

  public downloadLinkByAccessToken(id: number, accessToken: string) {
    return `${baseApiMobileUrl}/${id}/download?accessToken=${accessToken}`;
  }

  public streamVideoLink(id: number) {
    return `${baseApiUrl}/${id}/stream-video`;
  }

  public m3u8VideoLink(id: number) {
    return `${baseApiUrl}/${id}/m3u8/access-token/_/file/video.m3u8`;
  }

  public download(id: number, fileName) {
    const tempLink = document.createElement('a');
    tempLink.href = `${baseApiUrl}/${id}/download`;
    tempLink.download = fileName;
    tempLink.target = '_blank';
    tempLink.click();
  }

  public thumbnail(id: number) {
    return `${baseApiUrl}/${id}/thumbnail`;
  }

  public setProjectThumbnail(id: number) {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/${id}/set-project-thumbnail`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieve(paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public filterGet(paginationQuery?: any, filter?: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(baseApiUrl + `/filter?${buildPaginationQueryOpts(paginationQuery)}&filter=${filter}&sort=flightDate,desc`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public filter(paginationQuery?: any, filter?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(baseApiUrl + `/filter?${buildPaginationQueryOpts(paginationQuery)}`, filter)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public createAndUpload(entity: IFileGeodetic, file, onUploadProgress): Promise<IFileGeodetic> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append(
      'fileGeodeticDTO',
      new Blob([JSON.stringify(entity)], {
        type: 'application/json',
      })
    );
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: onUploadProgress,
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public create(entity: IFileGeodetic): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: IFileGeodetic): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/${entity.id}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public partialUpdate(entity: IFileGeodetic): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .patch(`${baseApiUrl}/${entity.id}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public changeProject(id?: any, projectId?: any): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/${id}/change-project/${projectId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public duplicate(id: number): Promise<IFileGeodetic> {
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/${id}/duplicate`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public search(paginationQuery?: any, keyword?: string, projectId?: number): Promise<any> {
    if (keyword) {
      return new Promise<any>((resolve, reject) => {
        axios
          .get(
            baseApiUrl +
              `-search?fileNameLike=${keyword}${projectId ? `&projectId=${projectId}` : ''}
          ${buildPaginationQueryOpts(paginationQuery)}`
          )
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    } else if (projectId) {
      return this.filter(paginationQuery, { projectId });
    } else {
      return this.retrieve(paginationQuery);
    }
  }

  public uploadThumbnail(id: number, file): Promise<IFileGeodetic> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return new Promise<IFileGeodetic>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/${id}/set-thumbnail-from-file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
